import { useCallback } from 'react';
import { css } from '@emotion/react';
import dayjs from 'dayjs';
import { CallPathV1 } from '@frontend/api-call-path';
import { CallPathMapping } from '@frontend/call-path-mapping';
import { useTranslation } from '@frontend/i18n';
import { formatPhoneNumber } from '@frontend/phone-numbers';
import { PhonesIllustration, PhonesTranscriptionNotAvailable } from '@frontend/phones#svg';
import { theme } from '@frontend/theme';
import { ContentLoader, Text } from '@frontend/design-system';
import { useCallInsightPanelShallowStore } from '../../hooks/use-call-insight-panel-store';
import { ScreenBaseProps, ScreenType } from '../../types';
import { ScreenLayout } from '../screen-layout';

type CallPathMappingScreenProps = ScreenBaseProps<ScreenType.CALL_PATH_MAPPING>;
const IMAGE_SIZE = 300;

export const CallPathMappingScreen = ({ data, panelStyles, onClose, setScreen }: CallPathMappingScreenProps) => {
  const { callStartTimeFormatted, channelId, callFrom: _callFrom, startTime, callStatus: _callStatus } = data ?? {};

  const { closeContext } = useCallInsightPanelShallowStore('closeContext');
  const { t } = useTranslation('calls');

  const handleClose = useCallback(() => {
    closeContext();
    onClose?.();
  }, [closeContext, onClose]);

  const { data: callPathDetails, status } = CallPathV1.CallPathQueries.useGetCallPathQuery({
    callStartTime: startTime || '',
    channelId: channelId || '',
  });

  const callFrom = _callFrom || formatPhoneNumber(callPathDetails?.patientPhoneNumber) || t('Unknown');
  const callStatus = callPathDetails?.callStatus || _callStatus;
  const hasAllocatedTimePassed = checkForAfterTimeLimit(startTime);
  const hasCallPathEvents = (callPathDetails?.callEvents?.length ?? 0) > 0;

  const statusToComponentMap: Record<typeof status, JSX.Element | null> = {
    loading: <ContentLoader show />,
    error: (
      <StateDisplay
        image={<PhonesTranscriptionNotAvailable height={IMAGE_SIZE} customFallback={<></>} />}
        text={t('Call Path is unavailable due to system error.')}
      />
    ),
    success: hasAllocatedTimePassed ? (
      <StateDisplay
        image={<PhonesIllustration height={IMAGE_SIZE} customFallback={<></>} />}
        text={t("It doesn't look like there's any data to show here")}
      />
    ) : (
      <StateDisplay
        image={<PhonesIllustration height={IMAGE_SIZE} customFallback={<></>} />}
        text={t("Hold tight, we're working on getting this data")}
      />
    ),
    idle: null,
  };

  const renderContent = () =>
    hasCallPathEvents ? <CallPathMapping callPathDetails={callPathDetails} /> : statusToComponentMap[status] ?? null;

  return (
    <ScreenLayout
      panelStyles={[panelStyles, containerStyles]}
      header={CallPathMapping.Header({
        onBack: () => setScreen({ screenType: ScreenType.CALL_INSIGHTS }),
        onClose: handleClose,
        callFrom,
        callStartTime: callStartTimeFormatted,
        callStatus,
        aiCallMetadata: data?.aiCallMetadata,
      })}
      content={renderContent()}
      contentStyles={contentStyles}
      trackingId='CallPathOverviewPanel'
    />
  );
};

const StateDisplay = ({ image, text }: { image: React.ReactNode; text: string }) => (
  <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2), alignItems: 'center' }}>
    {image}
    <Text textAlign='center' color='light' weight='bold'>
      {text}
    </Text>
  </div>
);

/* Backend suggest adding a 5 min time gap to wait for the processes to finish if no data is available due to current limitations */
function checkForAfterTimeLimit(timestamp: string | undefined) {
  if (!timestamp) return;
  const now = dayjs();
  const givenTime = dayjs(timestamp);
  const fiveMinutesAfter = givenTime.add(5, 'minute');
  return now.isAfter(fiveMinutesAfter);
}

const containerStyles = css`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const contentStyles = css`
  padding: ${theme.spacing(2)};
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: ${theme.spacing(2)};
  background-color: ${theme.colors.neutral5};

  & > .event-box-item::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    width: 2px;
    height: 16px;
    background-color: ${theme.colors.neutral70};
  }

  & > .event-box-item.analytics-info-icon::after {
    top: 20px;
  }

  & > .event-box-item:last-of-type::after {
    display: none;
  }
`;
